import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Title, Button, Spin } from '@bancobarigui/barihaus';
import { PlusOutlined, WarningOutlined } from '@ant-design/icons';

import { ProtectedRouteContainer } from '@/components/ProtectedRouteContainer';
import { PageHeader } from '@/components/PageHeader';
import { ServiceCheck, FeatureAlert } from '@/shared/components';
import { ElementGuard } from '@/components/PermissionGuard';
import { InspectionsTable } from '@/components/InspectionsTable';
import { UserPermissions } from '@/shared/enums/UserPermissions';
import { DetailInspection } from '@/components/DetailInspection';
import { AssignInspectionModal } from '@/components/AssignInspectionModal';
import { ScheduleInspectionModal } from '@/components/ScheduleInspectionModal';
import { ProviderResponseModal } from '@/components/ProviderResponseModal';
import { AssignInspectorModal } from '@/components/AssignInspectorModal';
import { InspectorAcceptModal } from '@/components/InspectorAcceptModal';
import { InspectorRejectModal } from '@/components/InspectorRejectModal';
import { CreateIssueModal } from '@/components/CreateIssueModal';

import { GoogleCalendarButton } from '@/components/GoogleCalendarButton';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { DetailFilters } from './components';
import { useDashboardDetail } from './useDashboardDetail';
import { useInspectionActions } from './useInspectionActions';

export default function DashboardDetail() {
  const navigate = useNavigate();
  const { isInspectorUser } = usePermissions();

  const {
    cardConfig,
    inspections,
    totalItems,
    currentPage,
    pageSize,
    isLoading,
    filters,
    statusOptions,
    inspectionTypeOptions,
    propertyTypeOptions,
    inspectionReasonOptions,
    providerOptions,
    columnFilters,
    onColumnFilterChange,
    loadFilterOptions,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
    handlePageChange,
    handleGoBack,
    handleToggleFavorite,
    refreshData,
  } = useDashboardDetail();

  const handleNewInspection = () => {
    navigate('/request-inspection');
  };

  const {
    isScheduleModalOpen,
    isAssignModalOpen,
    isDetailModalOpen,
    isProviderAcceptModalOpen,
    isProviderRejectModalOpen,
    isAssignInspectorModalOpen,
    isInspectorAcceptModalOpen,
    isInspectorRejectModalOpen,
    isCreateIssueModalOpen,
    selectedInspectionId,
    selectedItemId,
    selectedAssignmentId,
    selectedProviderId,
    selectedInspection,
    selectedInspectionForIssue,
    setOnRefresh,
    handleSchedule,
    handleAssign,
    handleRowClick,
    handleProviderAccept,
    handleProviderReject,
    handleAssignInspector,
    handleInspectorAccept,
    handleInspectorReject,
    handleCreateIssue,
    handleCloseScheduleModal,
    handleCloseAssignModal,
    handleCloseDetailModal,
    handleCloseProviderAcceptModal,
    handleCloseProviderRejectModal,
    handleCloseAssignInspectorModal,
    handleCloseInspectorAcceptModal,
    handleCloseInspectorRejectModal,
    handleCloseCreateIssueModal,
    handleScheduleSuccess,
    handleAssignSuccess,
    handleProviderResponseSuccess,
    handleAssignInspectorSuccess,
    handleInspectorResponseSuccess,
    handleCreateIssueSuccess,
  } = useInspectionActions();

  useEffect(() => {
    setOnRefresh(() => refreshData);
  }, [setOnRefresh, refreshData]);

  if (!cardConfig) {
    return (
      <ProtectedRouteContainer>
        <div className="flex flex-col items-center justify-center py-20">
          <WarningOutlined className="text-6xl text-gray-300 mb-4" />
          <Title level={3} className="text-gray-500">
            Card não encontrado
          </Title>
          <Button buttonColor="primary" onClick={handleGoBack} className="mt-4">
            Voltar ao Início
          </Button>
        </div>
      </ProtectedRouteContainer>
    );
  }

  return (
    <ProtectedRouteContainer
      pageHeader={
        <PageHeader
          title={cardConfig.title}
          subtitle={cardConfig.description}
          actions={
            <>
              {isInspectorUser() && <GoogleCalendarButton />}
              <ElementGuard permissions={[UserPermissions.inspections_create_bank]}>
                <Button
                  buttonColor="primary"
                  icon={<PlusOutlined />}
                  onClick={handleNewInspection}
                >
                  Nova Inspeção
                </Button>
              </ElementGuard>
            </>
          }
        />
      }
    >
      <FeatureAlert services={['inspection']} />
      <ServiceCheck
        services={['inspection']}
        featureName="Detalhes de Inspecoes"
      >
      <DetailFilters
        filters={filters}
        statusOptions={statusOptions}
        isLoading={isLoading}
        onFilterChange={handleFilterChange}
        onClear={handleClearFilters}
        onApply={handleApplyFilters}
      />

      <Spin spinning={isLoading} tip="Carregando...">
        <InspectionsTable
          inspections={inspections}
          isLoading={isLoading}
          onSchedule={handleSchedule}
          onAssign={handleAssign}
          onProviderAccept={handleProviderAccept}
          onProviderReject={handleProviderReject}
          onAssignInspector={handleAssignInspector}
          onInspectorAccept={handleInspectorAccept}
          onInspectorReject={handleInspectorReject}
          onCreateIssue={handleCreateIssue}
          onRowClick={handleRowClick}
          onToggleFavorite={handleToggleFavorite}
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          statusOptions={statusOptions}
          inspectionTypeOptions={inspectionTypeOptions}
          propertyTypeOptions={propertyTypeOptions}
          inspectionReasonOptions={inspectionReasonOptions}
          providerOptions={providerOptions}
          columnFilters={columnFilters}
          onColumnFilterChange={onColumnFilterChange}
          loadFilterOptions={loadFilterOptions}
        />
      </Spin>

      <DetailInspection
        open={isDetailModalOpen}
        inspectionId={selectedInspectionId}
        itemId={selectedItemId}
        onClose={handleCloseDetailModal}
        onUpdate={refreshData}
      />

      <ScheduleInspectionModal
        open={isScheduleModalOpen}
        onClose={handleCloseScheduleModal}
        onSuccess={handleScheduleSuccess}
        itemId={selectedInspectionId || ''}
        inspectionData={selectedInspection}
      />

      <AssignInspectionModal
        open={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        onSuccess={handleAssignSuccess}
        itemId={selectedInspectionId || ''}
        inspectionData={selectedInspection}
      />

      <ProviderResponseModal
        open={isProviderAcceptModalOpen}
        onClose={handleCloseProviderAcceptModal}
        onSuccess={handleProviderResponseSuccess}
        assignmentId={selectedAssignmentId || ''}
        inspectionData={selectedInspection}
        actionType="accept"
      />

      <ProviderResponseModal
        open={isProviderRejectModalOpen}
        onClose={handleCloseProviderRejectModal}
        onSuccess={handleProviderResponseSuccess}
        assignmentId={selectedAssignmentId || ''}
        inspectionData={selectedInspection}
        actionType="reject"
      />

      <AssignInspectorModal
        open={isAssignInspectorModalOpen}
        onClose={handleCloseAssignInspectorModal}
        onSuccess={handleAssignInspectorSuccess}
        assignmentId={selectedAssignmentId || ''}
        {...(selectedProviderId && { providerId: selectedProviderId })}
      />

      <InspectorAcceptModal
        open={isInspectorAcceptModalOpen}
        onClose={handleCloseInspectorAcceptModal}
        onSuccess={handleInspectorResponseSuccess}
        assignmentId={selectedAssignmentId || ''}
      />

      <InspectorRejectModal
        open={isInspectorRejectModalOpen}
        onClose={handleCloseInspectorRejectModal}
        onSuccess={handleInspectorResponseSuccess}
        assignmentId={selectedAssignmentId || ''}
      />

      <CreateIssueModal
        open={isCreateIssueModalOpen}
        onClose={handleCloseCreateIssueModal}
        onSuccess={handleCreateIssueSuccess}
        inspectionItemId={selectedInspectionForIssue || ''}
      />
      </ServiceCheck>
    </ProtectedRouteContainer>
  );
}
